import React, { useState } from 'react';
import useSWR, { mutate } from 'swr';
import {
 Heading,
 Text,
 Box,
 Button,
 Flex,
 TableContainer,
 Table,
 Thead,
 Tbody,
 Tr,
 Td,
 Th,
 useToast,
} from '@chakra-ui/react';
import { fetchData } from '../../services/fetch';
import baseService from '../../services/base';
import { AttentionModal, SuccessModal, UserAccessModal } from '../Modals';
import TableMenu from '../Menu/TableMenu';

const getUserType = (roles) => {
 if (!roles || roles.length === 0) return '';
 return roles.some((role) => role.name === 'MASTER' || role.name === 'AVALIADOR')
  ? 'INTERNO'
  : 'EMPRESA';
};

const getUserProfile = (user) => {
 const roles = user?.roles;
 if (!roles || roles.length === 0) return '';

 const profiles = roles.map((role) => {
  switch (role.name) {
   case 'MASTER':
    return 'MASTER';
   case 'AVALIADOR':
    return 'AVALIADOR';
   case 'RESPONSAVEL':
    // Admin da empresa (vínculo links_admin_company) ≠ RESPONSÁVEL comum.
    return user?.isCompanyAdmin ? 'ADMIN' : 'RESPONSÁVEL';
   case 'RESPONDENTE':
    return 'RESPONDENTE';
   default:
    return '';
  }
 });

 return profiles.filter(Boolean).join('+');
};

const UserTypeBox = ({ type }) => (
 <Text
  as='span'
  px='12px'
  py='8px'
  borderRadius='4px'
  fontSize='13px'
  lineHeight='15.26px'
  fontWeight={600}
  bg={type === 'INTERNO' ? '#FDEEEA' : '#E5F1F8'}
  color={type === 'INTERNO' ? '#EA582B' : '#0073BB'}
  display='flex'
  alignItems='center'
  justifyContent='center'
 >
  {type}
 </Text>
);

const ProfileBox = ({ profile, userType }) => {
 const hasMultipleRoles = profile.includes('+');

 const getColors = () => {
  // Se tem múltiplas roles
  if (hasMultipleRoles) {
   if (userType === 'INTERNO') {
    return {
     bg: 'rgba(236, 132, 13, 0.1)',
     color: '#EC840D',
    };
   }
   return {
    bg: 'rgba(0, 178, 239, 0.1)',
    color: '#00B2EF',
   };
  }

  // Se tem apenas uma role
  if (userType === 'INTERNO') {
   return {
    bg: '#FDEEEA',
    color: '#EA582B',
   };
  }
  return {
   bg: '#E5F1F8',
   color: '#0073BB',
  };
 };

 const { bg, color } = getColors();

 return (
  <Text
   as='span'
   px='12px'
   py='8px'
   borderRadius='4px'
   fontSize='13px'
   lineHeight='15.26px'
   fontWeight={600}
   bg={bg}
   color={color}
   display='flex'
   alignItems='center'
   justifyContent='center'
  >
   {profile}
  </Text>
 );
};

const StatusBadge = ({ isActive }) => (
 <Text
  as='span'
  px='12px'
  py='8px'
  borderRadius='4px'
  fontSize='13px'
  lineHeight='15.26px'
  fontWeight={600}
  bg={isActive ? '#E6F4EA' : '#F5F5F5'}
  color={isActive ? '#1E7E34' : '#999999'}
  display='flex'
  alignItems='center'
  justifyContent='center'
 >
  {isActive ? 'Ativo' : 'Inativo'}
 </Text>
);

export default function UserTable({ companyRegister, idCompanyGroup }) {
 const toast = useToast();

 const [openUserAddModal, setOpenUserFormAddModal] = useState(false);
 const [currentUser, setCurrentUser] = useState();
 const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);

 const [currentConfirmationMessage, setCurrentConfirmationMessage] = useState('');
 const [confirmationModalIsLoading, setConfirmationModalIsLoading] = useState(false);
 const [currentSuccessMessage, setCurrentSuccessMessage] = useState(
  'Informações editadas com sucesso'
 );
 const [successModalIsOpen, setSuccessModalOpen] = useState(false);

 const { data } = useSWR(
  idCompanyGroup ? `/user?group=${idCompanyGroup}` : null,
  fetchData
 );

 const closeUserFormModal = () => {
  setOpenUserFormAddModal(false);
 };

 const modifyUserAccess = (userInfo, isPasswordChange) => {
  setCurrentUser({
   ...userInfo,
   isPasswordChange: isPasswordChange,
  });
  setCurrentConfirmationMessage(
   isPasswordChange
    ? 'Você tem certeza de que deseja redefinir a senha deste usuário?'
    : 'Deseja inativar este usuário? Esta ação é permanente e o usuário perderá todo o acesso ao sistema.'
  );
  setConfirmationModalIsOpen(true);
 };

 const removeUser = async () => {
  try {
   setConfirmationModalIsLoading(true);
   if (currentUser && currentUser.isPasswordChange) {
    await baseService.patch(`/user/password`, {
     email: currentUser.email,
     password: 'GP@esg!2024',
     isPasswordReset: true,
    });
    setCurrentSuccessMessage('Senha redefinida com sucesso');
   } else {
    await baseService.patch(`/user/${currentUser.id_user}/deactivate`);
    setCurrentSuccessMessage('Usuário inativado com sucesso');
   }
   setConfirmationModalIsOpen(false);
   setSuccessModalOpen(true);
   mutate(`/user?group=${idCompanyGroup}`);
  } catch (error) {
   toast({
    title: 'Erro ao atualizar os dados',
    description: 'Tente novamente ou entre em contato com o suporte',
    status: 'error',
    duration: 5000,
    isClosable: true,
   });
  } finally {
   setConfirmationModalIsLoading(false);
  }
 };

 const reactivateUser = async (user) => {
  try {
   await baseService.patch(`/user/${user.id_user}/activate`);
   setCurrentSuccessMessage('Usuário reativado com sucesso');
   setSuccessModalOpen(true);
   mutate(`/user?group=${idCompanyGroup}`);
  } catch (error) {
   toast({
    title: 'Erro ao reativar o usuário',
    description: 'Tente novamente ou entre em contato com o suporte',
    status: 'error',
    duration: 5000,
    isClosable: true,
   });
  }
 };

 const handleCloseConfirmation = () => {
  setConfirmationModalIsOpen(false);
  setCurrentUser(null);
  setCurrentConfirmationMessage('');
 };

 return (
  <Box>
   <Flex h='49px' justify={'space-between'} align='center' marginBottom='28px'>
    <Heading as='h2' fontSize='20px' lineHeight='24.8px'>
     Usuários
    </Heading>

    <Button
     onClick={() => setOpenUserFormAddModal(true)}
     variant='primary'
     w='208px'
     h='49px'
     isDisabled={!companyRegister}
    >
     Cadastrar usuários
    </Button>
   </Flex>

   <TableContainer>
    <Table>
     <Thead height='51px' bg='#EAEAEA' p={0}>
      <Tr p={0} fontWeight={500}>
       <Th fontSize='15px' lineHeight='19.38px' color='black.100' w='52%'>
        Acesso
       </Th>
       <Th fontSize='15px' lineHeight='19.38px' color='black.100' w='12%'>
        USUÁRIO
       </Th>
       <Th fontSize='15px' lineHeight='19.38px' color='black.100' w='16%'>
        PERFIL
       </Th>
       <Th fontSize='15px' lineHeight='19.38px' color='black.100' w='10%'>
        STATUS
       </Th>
       <Th fontSize='15px' lineHeight='19.38px' color='black.100' w='10%'>
        Ações
       </Th>
      </Tr>
     </Thead>
     {data && (
      <Tbody>
       {data?.map((user, index) => {
        const userType = getUserType(user?.roles);
        const userProfile = getUserProfile(user);
        const isInactive = user?.is_active === false;

        return (
         <Tr
          key={index}
          borderBlock='1px solid rgba(0,0,0,0)'
          height='48px'
          fontWeight={500}
          padding='0px 24px'
         >
          <Td fontSize='sm' fontWeight={600} whiteSpace='nowrap' py='8px'>
           {user?.email}
          </Td>
          <Td whiteSpace='nowrap' py='8px'>
           <UserTypeBox type={userType} />
          </Td>
          <Td whiteSpace='nowrap' py='8px'>
           <ProfileBox profile={userProfile} userType={userType} />
          </Td>
          <Td whiteSpace='nowrap' py='8px'>
           <StatusBadge isActive={user?.is_active !== false} />
          </Td>
          <Td py='8px' display='flex' justifyContent='center'>
           <TableMenu
            menuItems={
             isInactive
              ? [
                 {
                  label: 'Reativar',
                  handler: () => reactivateUser(user),
                 },
                ]
              : [
                 {
                  label: 'Inativar',
                  handler: () => modifyUserAccess(user),
                 },
                 {
                  label: 'Resetar senha',
                  handler: () => modifyUserAccess(user, true),
                 },
                ]
            }
           />
          </Td>
         </Tr>
        );
       })}
      </Tbody>
     )}
    </Table>
   </TableContainer>

   <UserAccessModal
    onClose={closeUserFormModal}
    isOpen={openUserAddModal}
    companyGroupId={idCompanyGroup}
    id_company={companyRegister}
   />

   <AttentionModal
    isOpen={confirmationModalIsOpen}
    onClose={handleCloseConfirmation}
    title={currentConfirmationMessage}
    isLoading={confirmationModalIsLoading}
    submit={removeUser}
   />
   <SuccessModal
    isOpen={successModalIsOpen}
    onClose={() => setSuccessModalOpen(false)}
    title={currentSuccessMessage}
   />
  </Box>
 );
}
